import { useMemo, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SortableTableHead } from "@/components/shared/SortableTableHead";
import { ConsolidatedLandscape } from "@/types";
import { toast as sonnerToast } from "sonner";
import { MAX_LANDSCAPES_PER_REQUEST } from "@/utils/keywordValidation";
import { landscapeApi } from "@/resources/landscape";
import { useBulkLandscapeExport } from "../useBulkLandscapeExport";
import { useAppData } from "@/contexts/AppDataContext";
import { ConsolidatedLandscapeRow } from "./ConsolidatedLandscapeRow";
import { LandscapeBulkBar } from "./LandscapeBulkBar";
import { StickyToolbarProvider } from "@/hooks/useStickyToolbar";
import { CountBadge } from "@/components/shared/CountBadge";
import { LandscapeStateRow } from "./LandscapeStateRow";
import { landscapeGroupRowKind } from "@/utils/landscapeRunStatus";
import { searchPhraseKey } from "@/utils/searchPhrase";

const INITIAL_VISIBLE_ROWS = 5;

type SortField = "phrase" | "top4" | "page1" | "keywords" | "date";
type SortDirection = "asc" | "desc" | null;

interface SortState {
  field: SortField | null;
  direction: SortDirection;
}

export interface KeywordLandscapeTableProps {
  landscapes: ConsolidatedLandscape[];
  heroAsin?: string;
  heroProductId?: number;
  showAllRows?: boolean;
  isLoading?: boolean;
  onViewInstance?: (instanceId: string) => void;
  onCreateAbTest?: (keyword: string) => void;
  readOnly?: boolean;
  /** Groups with no finished version; the failed and creating ones render above `landscapes`. */
  unfinishedLandscapes?: ConsolidatedLandscape[];
  duplicateRows?: { key: string; phrase: string }[];
  onDismissDuplicate?: (key: string) => void;
}

/** One failed or creating group: its own hook instances, so one row's pending request disables only that row. */
function UnfinishedGroupRow({
  group,
  kind,
  heroAsin,
  heroProductId,
  readOnly,
}: {
  group: ConsolidatedLandscape;
  kind: "failed" | "creating";
  heroAsin?: string;
  heroProductId?: number;
  readOnly: boolean;
}) {
  const { selectedBrandId } = useAppData();
  const { mutate: dismiss, isPending: isDismissing } = landscapeApi.useDismissLandscapeGroup();
  const { mutate: retry, isPending: isRetrying } = landscapeApi.useRetryLandscape();
  const groupKey = searchPhraseKey(group.prioritySearchPhrase);
  // `isPending` reaches the render on a batched schedule, so a second click in the same tick still
  // sees an enabled Retry. The ref answers the second click; the state disables the button at once
  // and holds it through the gap between the request succeeding and the row leaving Failed. A kind
  // change remounts the row (the keys differ), so a row that fails again starts unlocked.
  const retryingRef = useRef(false);
  const [retryLocked, setRetryLocked] = useState(false);
  const releaseRetry = () => {
    retryingRef.current = false;
    setRetryLocked(false);
  };

  return (
    <LandscapeStateRow
      kind={kind}
      phrase={group.prioritySearchPhrase}
      statusInfo={group.instances?.[0]?.status_info}
      readOnly={readOnly}
      dismissDisabled={isDismissing || isRetrying || retryLocked}
      onDismiss={() => {
        if (heroProductId == null) return;
        dismiss({
          landscapeIds: (group.instances ?? []).map((instance) => Number(instance.id)),
          groupKey,
          heroProductId,
        });
      }}
      retryDisabled={isRetrying || retryLocked || !heroAsin || !selectedBrandId || heroProductId == null}
      onRetry={() => {
        if (!heroAsin || !selectedBrandId || heroProductId == null || retryingRef.current) return;
        retryingRef.current = true;
        setRetryLocked(true);
        retry(
          {
            searchPhrase: group.prioritySearchPhrase,
            heroAsin,
            brandId: selectedBrandId,
            heroProductId,
            groupKey,
          },
          {
            // Nothing created: the row stays Failed, so Retry must stay usable.
            onSuccess: (data) => {
              if (!data.created?.length) releaseRetry();
            },
            onError: releaseRetry,
          },
        );
      }}
    />
  );
}

function SkeletonRow({ readOnly = false }: { readOnly?: boolean }) {
  return (
    <TableRow>
      {!readOnly ? <TableCell className="px-3 py-2 w-10"><Skeleton className="h-4 w-4" /></TableCell> : null}
      <TableCell className="px-3 py-2 w-8" />
      <TableCell className="px-3 py-2"><Skeleton className="h-4 w-40" /></TableCell>
      <TableCell className="px-3 py-2"><Skeleton className="h-4 w-20" /></TableCell>
      <TableCell className="px-3 py-2"><Skeleton className="h-4 w-20" /></TableCell>
      <TableCell className="px-3 py-2 text-center"><Skeleton className="h-4 w-10 mx-auto" /></TableCell>
      <TableCell className="px-3 py-2"><Skeleton className="h-4 w-20" /></TableCell>
      <TableCell className="px-3 py-2"><Skeleton className="h-4 w-16" /></TableCell>
    </TableRow>
  );
}

export function KeywordLandscapeTable({
  landscapes,
  heroAsin,
  heroProductId,
  showAllRows = false,
  isLoading = false,
  onViewInstance,
  onCreateAbTest,
  readOnly = false,
  unfinishedLandscapes = [],
  duplicateRows = [],
  onDismissDuplicate,
}: KeywordLandscapeTableProps) {
  const [showAll, setShowAll] = useState(showAllRows);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState>({ field: null, direction: null });
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const {
    mutate: bulkDeleteLandscapes,
    isPending: isBulkDeleting,
    variables: bulkDeleteVariables,
  } = landscapeApi.useBulkDeleteLandscapes();
  const { isExporting, handleBulkExport } = useBulkLandscapeExport(landscapes);
  const { mutate: bulkRefreshLandscapes, isPending: isBulkRefreshing } = landscapeApi.useBulkRefreshLandscapes();
  const { selectedBrandId } = useAppData();

  const pendingDeleteIds = useMemo(() => {
    if (!isBulkDeleting || !bulkDeleteVariables) return new Set<string>();
    const idSet = new Set(bulkDeleteVariables.landscapeIds);
    return new Set(
      landscapes
        .filter((l) => (l.instances ?? []).some((instance) => idSet.has(Number(instance.id))))
        .map((l) => l.id),
    );
  }, [isBulkDeleting, bulkDeleteVariables, landscapes]);

  const failedGroups = unfinishedLandscapes.filter((g) => landscapeGroupRowKind(g) === "failed");
  const creatingGroups = unfinishedLandscapes.filter((g) => landscapeGroupRowKind(g) === "creating");

  const toggleSort = (field: SortField) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      if (prev.direction === "asc") return { field, direction: "desc" };
      return { field: null, direction: null };
    });
  };

  const sortedLandscapes = [...landscapes].sort((a, b) => {
    if (!sort.field || !sort.direction) return 0;
    const dir = sort.direction === "asc" ? 1 : -1;
    const aInst = (a.instances || [])[0];
    const bInst = (b.instances || [])[0];
    if (!aInst || !bInst) return 0;
    switch (sort.field) {
      case "phrase": return dir * a.prioritySearchPhrase.localeCompare(b.prioritySearchPhrase);
      case "top4": return dir * (aInst.top4Hero - bInst.top4Hero);
      case "page1": return dir * (aInst.page1Hero - bInst.page1Hero);
      case "keywords": return dir * (aInst.keywordCount - bInst.keywordCount);
      case "date": return dir * (new Date(aInst.date).getTime() - new Date(bInst.date).getTime());
      default: return 0;
    }
  });

  const visibleLandscapes = showAll || showAllRows
    ? sortedLandscapes
    : sortedLandscapes.slice(0, INITIAL_VISIBLE_ROWS);
  const hasMore = !showAllRows && landscapes.length > INITIAL_VISIBLE_ROWS;

  const handleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(landscapes.map((l) => l.id)) : new Set());
  };

  const allSelected = landscapes.length > 0 && selectedIds.size === landscapes.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleBulkRefresh = () => {
    if (!heroAsin || !selectedBrandId) return;

    const selected = landscapes.filter((l) => selectedIds.has(l.id));
    if (selected.length === 0) return;
    if (selected.length > MAX_LANDSCAPES_PER_REQUEST) {
      sonnerToast.error(
        `No more than ${MAX_LANDSCAPES_PER_REQUEST} landscapes at a time \u2014 deselect ${selected.length - MAX_LANDSCAPES_PER_REQUEST}.`,
      );
      return;
    }

    bulkRefreshLandscapes(
      {
        landscapes: selected.map((l) => ({
          search_phrase: l.prioritySearchPhrase,
          hero_asin: heroAsin,
          brand_id: selectedBrandId,
        })),
        heroProductId,
      },
      { onSettled: () => setSelectedIds(new Set()) },
    );
  };

  const handleBulkDelete = () => {
    const landscapeIds = landscapes
      .filter((l) => selectedIds.has(l.id))
      .flatMap((l) => l.instances ?? [])
      .map((instance) => instance.id)
      .filter((id) => id != null)
      .map((id) => Number(id));

    if (landscapeIds.length > 0) {
      bulkDeleteLandscapes(
        { landscapeIds, heroProductId },
        { onSettled: () => setSelectedIds(new Set()) },
      );
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleDeleteLandscape = (landscapeId: string) => {
    const landscape = landscapes.find((l) => l.id === landscapeId);
    const landscapeIds = (landscape?.instances ?? [])
      .map((instance) => instance.id)
      .filter((id) => id != null)
      .map((id) => Number(id));

    if (landscapeIds.length === 0) return;

    bulkDeleteLandscapes({ landscapeIds, heroProductId });
  };

  // AUT-346: a render helper, NOT a component defined in render. As a component it got a new
  // identity every render, so React remounted the whole table body (and every row's local state --
  // expanded versions, open action menu, open Schedule modal) on any parent re-render, e.g. the
  // background kps-recalculation poll. Called as a function it's just JSX, so state survives.
  const renderTableBody = () => {
    if (isLoading) {
      return <>{Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} readOnly={readOnly} />)}</>;
    }
    if (sortedLandscapes.length === 0 && failedGroups.length === 0 && creatingGroups.length === 0 && duplicateRows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={readOnly ? 7 : 8} className="h-24 text-center text-muted-foreground text-xs">
            No landscapes found.
          </TableCell>
        </TableRow>
      );
    }
    return (
      <>
        {failedGroups.map((group) => (
          <UnfinishedGroupRow
            key={`failed-${group.id}`}
            group={group}
            kind="failed"
            heroAsin={heroAsin}
            heroProductId={heroProductId}
            readOnly={readOnly}
          />
        ))}
        {duplicateRows.map((row) => (
          <LandscapeStateRow
            key={`duplicate-${row.key}`}
            kind="duplicate"
            phrase={row.phrase}
            readOnly={readOnly}
            onDismiss={() => onDismissDuplicate?.(row.key)}
          />
        ))}
        {creatingGroups.map((group) => (
          <UnfinishedGroupRow
            key={`creating-${group.id}`}
            group={group}
            kind="creating"
            heroAsin={heroAsin}
            heroProductId={heroProductId}
            readOnly={readOnly}
          />
        ))}
        {visibleLandscapes.map((landscape, i) => (
          <ConsolidatedLandscapeRow
            key={landscape.id}
            landscape={landscape}
            animated={i >= INITIAL_VISIBLE_ROWS}
            isSelected={selectedIds.has(landscape.id)}
            onSelect={handleSelect}
            onViewInstance={onViewInstance}
            heroAsin={heroAsin}
            heroProductId={heroProductId}
            readOnly={readOnly}
            isBulkDeleting={isBulkDeleting}
            isPendingDelete={pendingDeleteIds.has(landscape.id)}
            onCreateAbTest={onCreateAbTest}
            onDeleteLandscape={handleDeleteLandscape}
          />
        ))}
      </>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <StickyToolbarProvider>
      {!readOnly && (
        <LandscapeBulkBar
          count={selectedIds.size}
          isBulkDeleting={isBulkDeleting}
          isBulkRefreshing={isBulkRefreshing}
          isExporting={isExporting}
          bulkDeleteOpen={bulkDeleteOpen}
          onBulkDeleteOpenChange={setBulkDeleteOpen}
          onClear={() => setSelectedIds(new Set())}
          onExport={() => handleBulkExport(selectedIds, () => setSelectedIds(new Set()))}
          onRefresh={handleBulkRefresh}
          onDelete={() => { handleBulkDelete(); setBulkDeleteOpen(false); }}
        />
      )}
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {!readOnly ? (
              <TableHead className="px-3 py-2 w-10">
                <Checkbox
                  aria-label="Select all landscapes"
                  checked={allSelected}
                  disabled={isBulkDeleting}
                  ref={(el) => {
                    if (el) (el as unknown as HTMLInputElement).indeterminate = someSelected;
                  }}
                  onCheckedChange={(checked) => handleSelectAll(!!checked)}
                />
              </TableHead>
            ) : null}
            <TableHead className="px-3 py-2 w-8" />
            <SortableTableHead field="phrase" activeField={sort.field} direction={sort.direction ?? "asc"} onSort={toggleSort} tooltip="Phrase used to identify the competitor set" className="px-3 py-2 text-xs font-medium">
              Priority Search Phrase
            </SortableTableHead>
            <SortableTableHead field="top4" activeField={sort.field} direction={sort.direction ?? "asc"} onSort={toggleSort} tooltip="How often the Hero ASIN ranks in the top 4 vs. competitor avg" className="px-3 py-2 text-xs w-[120px] font-medium">
              Top 4
            </SortableTableHead>
            <SortableTableHead field="page1" activeField={sort.field} direction={sort.direction ?? "asc"} onSort={toggleSort} tooltip="How often the Hero ASIN ranks on page 1 vs. competitor avg" className="px-3 py-2 text-xs w-[120px] font-medium">
              Page 1
            </SortableTableHead>
            <SortableTableHead field="keywords" activeField={sort.field} direction={sort.direction ?? "asc"} onSort={toggleSort} tooltip="Total keywords found for hero and competitor ASINs" className="px-3 py-2 text-xs w-[100px] text-center font-medium">
              Keywords
            </SortableTableHead>
            <SortableTableHead field="date" activeField={sort.field} direction={sort.direction ?? "asc"} onSort={toggleSort} tooltip="Date the landscape was generated" className="px-3 py-2 text-xs w-[120px] font-medium">
              Date
            </SortableTableHead>
            <TableHead className="px-3 py-2 text-xs font-medium text-foreground w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {renderTableBody()}
        </TableBody>
      </Table>
      {hasMore && (
        <div className="flex justify-center py-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-primary gap-1 transition-colors duration-200"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <><ChevronUp className="h-3 w-3" />Collapse</>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                Expand <CountBadge size="sm" count={landscapes.length - INITIAL_VISIBLE_ROWS} /> more
              </>
            )}
          </Button>
        </div>
      )}
      </StickyToolbarProvider>
    </TooltipProvider>
  );
}
